import { analytics } from '@qbe/qal';
import type { BaseQueryApi } from '@reduxjs/toolkit/query';
import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { retry } from '@reduxjs/toolkit/query/react';
import { v4 } from 'uuid';

import { formatTrackApiError, getTrackPayload } from '~/utils/analytics/trackUtils';

import { targetEnv } from '../../config';

// Utility to generate a new tracking ID

type CustomBaseQueryOptions = {
  baseUrl: string;
  defaultHeaders: (headers: Headers, context: { getState: () => unknown }) => Headers;
  reducerPath: string;
  useRetry?: boolean;
};

type AnalyticsApi = Pick<BaseQueryApi, 'type' | 'endpoint'>;

function prepareHeaders(
  reducerPath: string,
  args: string | FetchArgs,
  defaultHeaders: (headers: Headers, context: { getState: () => unknown }) => Headers,
  getState: () => unknown
): { headers: Headers; trackingId: string } {
  let headers = new Headers(
    typeof args !== 'string' && args.headers ? (args.headers as HeadersInit) : {}
  );
  headers = defaultHeaders(headers, { getState });

  if (reducerPath === 'sitecoreApi') {
    return { headers, trackingId: '' };
  }

  const trackingId = headers.get('X-TrackingID') ?? `${v4()}-${targetEnv}`;
  headers.set('X-TrackingID', trackingId);

  return { headers, trackingId };
}

function buildAnalyticsPayload(
  reducerPath: string,
  api: AnalyticsApi,
  baseUrl: string,
  url: string,
  trackingId: string
) {
  const endpointType = api.type === 'mutation' ? 'executeMutation' : 'executeQuery';
  const apiName = `${reducerPath}/${endpointType}/${api.endpoint}`;
  const apiEndpoint = `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
  return getTrackPayload(apiName, apiEndpoint, trackingId);
}

export const createCustomBaseQuery = ({
  baseUrl,
  defaultHeaders,
  reducerPath,
  useRetry = false,
}: CustomBaseQueryOptions): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => {
  let baseQuery = fetchBaseQuery({ baseUrl });
  if (useRetry) baseQuery = retry(baseQuery);

  return async (args, api, extraOptions) => {
    const { headers, trackingId } = prepareHeaders(reducerPath, args, defaultHeaders, api.getState);
    const requestArgs: FetchArgs =
      typeof args === 'string' ? { url: args, headers } : { ...args, headers };

    const url = typeof requestArgs.url === 'string' ? requestArgs.url : '';
    const payload = buildAnalyticsPayload(reducerPath, api, baseUrl, url, trackingId);

    analytics.trackApiStart(payload);
    const result = await baseQuery(requestArgs, api, extraOptions);

    result.error
      ? analytics.trackApiError(formatTrackApiError(result.error, payload.data))
      : analytics.trackApiComplete(payload);

    return result;
  };
};
